// src/utils/axios.js

import axios from "axios";
import { API_BASE_URL } from "./urls";
import { logout } from "../store/slices/authSlice";

// Lazy store accessor — fixes circular dependency
// axios -> store -> slice -> axios causes "Cannot access store before initialization"
// Solution: don't import store at module load time; require it only when the interceptor fires
const getStore = () => require("../store/store").store;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor — attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A single in-flight refresh, shared by every 401 that lands while it runs, so
// a burst of parallel requests doesn't spend the one-shot refresh token N times.
let refreshPromise = null;

const forceLogout = () => {
  refreshPromise = null;
  getStore().dispatch(logout());
  window.location.href = "/vendor-signin";
};

const runRefresh = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) throw new Error("NO_REFRESH_TOKEN");

  // Bare axios: `api`'s own interceptors would attach the dead access token and
  // recurse back into this handler when the refresh call fails.
  const { data } = await axios.post(
    `${API_BASE_URL}/vendors/refresh`,
    { refreshToken },
    { headers: { "Content-Type": "application/json" } }
  );

  const accessToken = data?.data?.accessToken;
  if (!accessToken) throw new Error("NO_ACCESS_TOKEN_IN_REFRESH");

  localStorage.setItem("accessToken", accessToken);
  return accessToken;
};

// Response interceptor — refresh once on 401, and only log out if that fails
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network failure / timeout: there is no response, and no session to end.
    if (!error.response || !originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response.status;

    // A 401 from /vendors/login means "wrong password", not "session expired".
    // Redirecting here would reload the sign-in page and wipe the form error.
    const isAuthRoute = /\/vendors\/(refresh|logout|login|register)/.test(
      originalRequest.url || ""
    );

    if (status !== 401 || originalRequest._retry || isAuthRoute) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = runRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const accessToken = await refreshPromise;

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${accessToken}`,
      };
      return api(originalRequest);
    } catch (refreshError) {
      // The session is genuinely over.
      forceLogout();
      return Promise.reject(error);
    }
  }
);

export default api;
